import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiArrowLeft, HiCheck } from "react-icons/hi";
import { PageLayout, ActionButton } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";
import "./CreatePaymentMethod.scss";

export const CreatePaymentMethod: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['paymentMethods', 'common']);
  const { token } = useAuth();
  const isRTL = i18n.language === 'ar';

  const [formData, setFormData] = useState({
    name: "",
    percent: 0,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'percent' ? Number(value) : value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      newErrors.name = t('paymentMethods:validation.nameRequired');
    } else if (formData.name.length < 2) {
      newErrors.name = t('paymentMethods:validation.nameMinLength');
    }

    if (formData.percent === null || formData.percent === undefined || formData.percent < 0) {
      newErrors.percent = t('paymentMethods:validation.percentRequired');
    } else if (formData.percent > 100) {
      newErrors.percent = t('paymentMethods:validation.percentMaxValue');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitItem = async () => {
    // Validate form before submission
    if (!validateForm()) {
      showErrorToast(t('paymentMethods:validation.fillAllFields'));
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await adminApi.call('/paymentmethod', 'POST', formData);

      if (!response.success) {
        showErrorToast(response.error || t('paymentMethods:messages.createError'));
        return;
      }

      showSuccessToast(t('paymentMethods:messages.createSuccess'));
      setFormData({ name: "", percent: 0 }); // Reset form
      setErrors({});
    } catch (error: any) {
      showErrorToast(t('paymentMethods:messages.createError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: t('paymentMethods:breadcrumbs.paymentMethods'), path: '/payment-methods' },
    { label: t('paymentMethods:breadcrumbs.create') }
  ];

  return (
    <PageLayout
      title={t('paymentMethods:createPaymentMethod')}
      subtitle={t('paymentMethods:subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="modern-form">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">{t('paymentMethods:fields.name')} <span className="required">*</span></label>
            <input
              type="text"
              className={`form-control ${errors.name ? 'is-invalid' : ''}`}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={t('paymentMethods:placeholders.name')}
              required
            />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">{t('paymentMethods:fields.percent')} <span className="required">*</span></label>
            <input
              type="number"
              className={`form-control ${errors.percent ? 'is-invalid' : ''}`}
              name="percent"
              value={formData.percent}
              onChange={handleInputChange}
              placeholder={t('paymentMethods:placeholders.percent')}
              min="0"
              max="100"
              step="0.01"
              required
            />
            {errors.percent && <div className="invalid-feedback">{errors.percent}</div>}
          </div>
        </div>

        <div className="form-actions">
          <ActionButton
            variant="primary"
            icon={HiCheck}
            onClick={submitItem}
            loading={loading}
            disabled={loading}
          >
            {loading ? t('paymentMethods:buttons.submitting') : t('paymentMethods:buttons.submit')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
